// Browser-side counterpart of the Rails `filter_parameters` list and
// TelemetryParamScrubber: browser SDK events go straight to Sentry, so none of
// the server-side filtering applies to them. Keys are compared lowercased with
// `_`/`-` stripped, so `date_of_birth` also covers `dateOfBirth`.
export const FILTERED = '[Filtered]'

const SENSITIVE_SUBSTRINGS = [
  'password',
  'passport',
  'dateofbirth',
  'dateofissue',
  'dateofexpiration',
  'token',
  'channelkey',
  'scanid',
  'mrz',
  'documentnumber',
  'surname',
  'givennames',
  'firstname',
  'lastname',
  'middlename',
  'gender',
  'expirydate',
  'nationality',
  'personalnumber',
  'email',
  'phone',
  'secret',
  'signature',
  'signedseed',
  'apikey',
  'authorization',
  'cookie',
  'socketid',
  'channelname',
  'codeverifier',
  'codechallenge',
  'customprompt',
  'searchquery',
  'destinationcountry',
  'transferspot',
  'accountnumber',
  'accountholder',
  'iban',
  'swift',
  'ifsc',
  'clabe',
  'routingnumber',
  'routingcode',
  'bankcode',
  'taxid',
  'contactname',
  'address',
  'postalcode',
]

// Anchored so ids and counts such as `guest_count` or `status_code` stay visible.
const SENSITIVE_EXACT = new Set([
  'sex',
  'guest',
  'guests',
  'note',
  'notes',
  'propsdata',
  'code',
  'state',
  'query',
  'previousquery',
  'message',
  'content',
  'prompt',
  'search',
  'selections',
  'reply',
  'beneficiary',
  'bic',
  'bsb',
])

const URL_KEYS = new Set(['url', 'from', 'to', 'querystring', 'referer', 'referrer'])
const MAX_DEPTH = 20

type Scrubbable = Record<string, unknown>

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, '')
}

export function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key)
  return SENSITIVE_EXACT.has(normalized) || SENSITIVE_SUBSTRINGS.some(part => normalized.includes(part))
}

// Query param names come through URL-encoded, e.g. `guest%5Bemail%5D`.
function paramName(rawName: string): string {
  try {
    return decodeURIComponent(rawName)
  } catch {
    return rawName
  }
}

function isSensitiveParam(rawName: string): boolean {
  return paramName(rawName)
    .split(/[[\]]+/)
    .filter(Boolean)
    .some(isSensitiveKey)
}

function scrubParams(text: string, pattern: RegExp): string {
  return text.replace(pattern, (match, separator: string, name: string) =>
    isSensitiveParam(name) ? `${separator}${name}=${FILTERED}` : match,
  )
}

// Router paths (`#/payment-verification`) and short word-like anchors (`#day-3`)
// are kept for debugging; any other unkeyed fragment may be a credential.
const SAFE_FRAGMENT = /^(\/|(?=.{1,32}$)[A-Za-z]+(?:[-_](?:[A-Za-z]+|\d{1,3}))*$)/

function scrubFragment(url: string): string {
  const hashIndex = url.indexOf('#')
  if (hashIndex < 0) return url
  const fragment = url.slice(hashIndex + 1)
  if (!fragment || fragment.includes('=') || SAFE_FRAGMENT.test(fragment)) return url
  return `${url.slice(0, hashIndex + 1)}${FILTERED}`
}

// Routes whose path segment is itself a capability token, e.g.
// `/en/operators/invitations/:token.json` or `check_download/:token`.
const PATH_TOKEN =
  /(\/(?:invitations|check_download|avatars\/(?:users|guests))\/)[^/?#\s"']+?(\.json)?(?=(?::\d+){1,2}(?![\w:])|[/?#\s"'),]|$)/g

function scrubPathTokens(text: string): string {
  return text.replace(PATH_TOKEN, (_match, prefix: string, format = '') => `${prefix}${FILTERED}${format}`)
}

export function scrubUrl(url: string): string {
  return scrubFragment(scrubParams(scrubPathTokens(url), /([?&;#]|^)([^=&?#;]+)=([^&#;]*)/g))
}

const EMAIL = /[\w.%+-]+@[\w-]+(?:\.[\w-]+)*\.[A-Za-z]{2,}\b/g
const TEXT_PARAM = /([?&;#\s]|^)([^=&?#;\s]+)=([^&#;\s]*)/g
const TEXT_KEY = /(["']?)([A-Za-z_][\w-]*)\1[ \t]*:\s*/g
const TEXT_VALUE = /"[^"]*"|'[^']*'|\[Filtered\]|[^,;}\]\r\n]*[^\s,;}\]]/y

// A sensitive `key: value` is masked up to the end of its clause. Scanning
// resumes right after every non-sensitive key, so `Error: ... passport: X` is
// still caught.
function scrubPairs(text: string): string {
  let result = ''
  let cursor = 0
  TEXT_KEY.lastIndex = 0
  for (let key = TEXT_KEY.exec(text); key; key = TEXT_KEY.exec(text)) {
    if (!isSensitiveKey(key[2])) continue
    TEXT_VALUE.lastIndex = TEXT_KEY.lastIndex
    const value = TEXT_VALUE.exec(text)
    if (!value) continue
    const quote = /^["']/.test(value[0]) ? value[0][0] : ''
    result += `${text.slice(cursor, TEXT_KEY.lastIndex)}${quote}${FILTERED}${quote}`
    cursor = TEXT_KEY.lastIndex = TEXT_VALUE.lastIndex
  }
  return result + text.slice(cursor)
}

// Free text (exception messages, console lines, logs) can't be filtered by key,
// so mask sensitive `key: value` and `key=value` pairs plus any email address.
export function scrubText(text: string): string {
  return scrubPathTokens(scrubParams(scrubPairs(text), TEXT_PARAM)).replace(EMAIL, FILTERED)
}

function scrubUrlValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubUrl(value)
  if (Array.isArray(value)) {
    return value.map(entry =>
      Array.isArray(entry) && typeof entry[0] === 'string' && isSensitiveParam(entry[0]) ? [entry[0], FILTERED] : entry,
    )
  }
  return value
}

// Request bodies and console arguments often arrive as JSON strings, which the
// key filter would otherwise never look inside.
function scrubString(value: string, depth: number, seen: WeakSet<object>): string {
  const trimmed = value.trim()
  if (/^(\{[\s\S]*\}|\[[\s\S]*\])$/.test(trimmed)) {
    try {
      return JSON.stringify(scrubValue(JSON.parse(trimmed), depth + 1, seen))
    } catch {
      // Not JSON (e.g. "[object Object]"); treat it as free text.
    }
  }
  return scrubText(value)
}

function scrubValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') return scrubString(value, depth, seen)
  if (value === null || typeof value !== 'object') return value
  if (depth > MAX_DEPTH || seen.has(value)) return FILTERED
  seen.add(value)

  if (Array.isArray(value)) return value.map(item => scrubValue(item, depth + 1, seen))

  const result: Scrubbable = {}
  for (const [key, entry] of Object.entries(value)) {
    if (isSensitiveKey(key)) {
      // Flags such as `passportRequired` carry no personal data.
      result[key] = typeof entry === 'boolean' ? entry : FILTERED
    } else if (URL_KEYS.has(normalizeKey(key))) {
      result[key] = scrubUrlValue(scrubValue(entry, depth + 1, seen))
    } else {
      result[key] = scrubValue(entry, depth + 1, seen)
    }
  }
  return result
}

// Returns a filtered copy; never mutates the input, which may still be live
// component or store state.
export function scrubTelemetry<T>(value: T): T {
  return scrubValue(value, 0, new WeakSet()) as T
}

function scrubMessage<V>(value: V): V {
  return (typeof value === 'string' ? scrubText(value) : value) as V
}

type TelemetryEvent = {
  message?: unknown
  exception?: { values?: { value?: string }[] }
  request?: unknown
  extra?: unknown
  contexts?: unknown
  tags?: unknown
  breadcrumbs?: unknown
}

export function scrubEvent<T extends TelemetryEvent>(event: T): T {
  event.message = scrubMessage(event.message)
  event.exception?.values?.forEach(exception => {
    if (exception.value) exception.value = scrubText(exception.value)
  })
  if (event.request) event.request = scrubTelemetry(event.request)
  if (event.extra) event.extra = scrubTelemetry(event.extra)
  if (event.contexts) event.contexts = scrubTelemetry(event.contexts)
  if (event.tags) event.tags = scrubTelemetry(event.tags)
  if (Array.isArray(event.breadcrumbs)) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  return event
}

type TelemetryBreadcrumb = { message?: unknown; data?: unknown }

export function scrubBreadcrumb<T extends TelemetryBreadcrumb>(breadcrumb: T): T {
  if (!breadcrumb) return breadcrumb
  const scrubbed = { ...breadcrumb, message: scrubMessage(breadcrumb.message) }
  if (scrubbed.message === undefined) delete scrubbed.message
  if (breadcrumb.data) scrubbed.data = scrubTelemetry(breadcrumb.data)
  return scrubbed
}

type TelemetryLog = { message?: unknown; attributes?: unknown }

export function scrubLog<T extends TelemetryLog>(log: T): T {
  log.message = scrubMessage(log.message)
  if (log.attributes) log.attributes = scrubTelemetry(log.attributes)
  return log
}
